import * as React from 'react';

import Tooltip from 'af-components/Tooltip';

import styles from './styles.module.scss';

interface OwnProps {
	selectedColumns: {
		latestRtId: number;
		label: string;
	}[];
	previouslySelectedReportTypes: string[];
	doNotUseReportTypes: string[];
	unassociatedReportTypes: string[];
	outdatedFields: string[];
	onDisableSelected: (latestRtId: number) => void;
}

type Props = OwnProps;

type SectionKey =
	| 'selected'
	| 'previouslySelected'
	| 'doNotUse'
	| 'unassociated'
	| 'outdated';

const SELECTED_WARNING_TOOLTIP = <div className={styles.tooltip}>
	<b>Selected for Job Tracking</b>
	Cannot save template. A field must be selected for Job Tracking.
</div>;

const PREVIOUSLY_SELECTED_WARNING_TOOLTIP = <div className={styles.tooltip}>
	<b>Previously selected</b>
	Cannot save template. A field must be selected for Job Tracking.
</div>;

const UNASSOCIATED_WARNING_TOOLTIP = <div className={styles.tooltip}>
	<b>Unassociated</b>
	This report types are not associated. Please select a field or mark it as "Do not use" to continue.
</div>;

const OUTDATED_WARNING_TOOLTIP = <div className={styles.tooltip}>
	<b>Outdated</b>
	Some revisions are not up to date. They are missing an association. Please review and update them in Revisions.
</div>;

const FieldsPane: React.FC<Props> = (props: Props) => {

	const {
		selectedColumns,
		doNotUseReportTypes,
		unassociatedReportTypes,
		outdatedFields,
		onDisableSelected,
		previouslySelectedReportTypes,
	} = props;

	const [openSections, setOpenSections] = React.useState<Record<SectionKey, boolean>>({
		selected: true,
		previouslySelected: false,
		doNotUse: true,
		unassociated: true,
		outdated: false,
	});

	const toggleSection = React.useCallback((key: SectionKey) => () => {
		setOpenSections((prev) => {
			return {
				...prev,
				[key]: !prev[key],
			};
		});
	}, []);

	const renderCard = React.useCallback((name: string) => {
		return (
			<div className={styles.card} key={name}>
				{name}
			</div>
		);
	}, []);

	const onDisable = React.useCallback((latestRtId: number) => () => {
		onDisableSelected(latestRtId);
	}, [onDisableSelected]);

	const renderRemovableCard = React.useCallback((field: {
		latestRtId: number;
		label: string;
	}) => {
		return (
			<div className={styles.card} key={`${field.latestRtId}-${field.label}`}>
				<span className={styles['card-label']}>{field.label}</span>
				<span
					className={`icon-close ${styles['close-icon']}`}
					onClick={onDisable(field.latestRtId)}
				/>
			</div>
		);
	}, [onDisable]);

	const renderOutdatedCard = React.useCallback((name: string) => {
		return (
			<div className={`${styles.card} ${styles['card-outdated-field']}`} key={name}>
				<span className={styles['card-outdated-field-name']}>{name}</span>
				<span className={`icon-warning ${styles['warning-icon']}`} />
			</div>
		);
	}, []);

	const renderChevron = React.useCallback((isOpen: boolean) => {
		return (
			<span
				className={`${isOpen ? 'icon-up' : 'icon-down'} ${styles['pane-section__chevron']}`}
			/>
		);
	}, []);

	const selectedWarning = React.useMemo(() => selectedColumns.length === 0 && previouslySelectedReportTypes.length === 0, [
		selectedColumns,
		previouslySelectedReportTypes,
	]);
	const unassociatedWarning = React.useMemo(() => unassociatedReportTypes.length > 0, [unassociatedReportTypes]);
	const outdatedWarning = React.useMemo(() => outdatedFields.length > 0, [outdatedFields.length]);

	return (
		<div className={styles['field-pane-wrapper']}>
			<div className={styles['pane-section']}>
				<button
					className={`${styles['pane-section__header']} ${selectedWarning ? styles['pane-section__header--warning'] : ''}`}
					onClick={toggleSection('selected')}
					type="button"
				>
					<div className={styles['pane-section__header-left']}>
						<div className={styles['pane-section__title-row']}>
							<span className={styles['pane-section__title']}>
								Selected for Job Tracking ({selectedColumns.length})
							</span>
							{selectedWarning && (
								<Tooltip message={SELECTED_WARNING_TOOLTIP}>
									<span className={`icon-warning ${styles['pane-section__warning-icon']}`} />
								</Tooltip>
							)}
						</div>
					</div>
					{renderChevron(openSections.selected)}
				</button>
			</div>
			{openSections.selected && (
				<div className={styles['card-container']}>
					{selectedColumns.map((field) => {
						return renderRemovableCard(field);
					})}
				</div>
			)}

			<div className={styles['pane-section']}>
				<button
					className={`${styles['pane-section__header']} ${selectedWarning ? styles['pane-section__header--warning'] : ''}`}
					onClick={toggleSection('previouslySelected')}
					type="button"
				>
					<div className={styles['pane-section__header-left']}>
						<div className={styles['pane-section__title-row']}>
							<span className={styles['pane-section__title']}>
								Previously selected ({previouslySelectedReportTypes.length})
							</span>
							<Tooltip message="These report types don't have latest revision associated, but they have some older revisions associated.">
								<span className={`icon-info ${styles['info-icon']}`} />
							</Tooltip>
							{selectedWarning && (
								<Tooltip message={PREVIOUSLY_SELECTED_WARNING_TOOLTIP}>
									<span className={`icon-warning ${styles['pane-section__warning-icon']}`} />
								</Tooltip>
							)}
						</div>
					</div>
					{renderChevron(openSections.previouslySelected)}
				</button>

			</div>

			{openSections.previouslySelected && (
				<div className={styles['card-container']}>
					{previouslySelectedReportTypes.map((field) => {
						return renderCard(field);
					})}
				</div>
			)}

			<div className={styles['pane-section']}>
				<button
					className={styles['pane-section__header']}
					onClick={toggleSection('doNotUse')}
					type="button"
				>
					<div className={styles['pane-section__header-left']}>
						<div className={styles['pane-section__title-row']}>
							<span className={styles['pane-section__title']}>
								Do Not Use This Report Type ({doNotUseReportTypes.length})
							</span>
						</div>
					</div>
					{renderChevron(openSections.doNotUse)}
				</button>

			</div>

			{openSections.doNotUse && (
				<div className={styles['card-container']}>
					{doNotUseReportTypes.map((field) => {
						return renderCard(field);
					})}
				</div>
			)}

			<div className={styles['pane-section']}>
				<button
					className={`${styles['pane-section__header']} ${unassociatedWarning ? styles['pane-section__header--warning'] : ''}`}
					onClick={toggleSection('unassociated')}
					type="button"
				>
					<div className={styles['pane-section__header-left']}>
						<div className={styles['pane-section__title-row']}>
							<span className={styles['pane-section__title']}>
								Unassociated ({unassociatedReportTypes.length})
							</span>
							{unassociatedWarning && (
								<Tooltip message={UNASSOCIATED_WARNING_TOOLTIP}>
									<span className={`icon-warning ${styles['pane-section__warning-icon']}`} />
								</Tooltip>
							)}
						</div>
						<div className={styles['pane-section__subtitle']}>
							All must be associated
						</div>
					</div>
					{renderChevron(openSections.unassociated)}
				</button>

			</div>

			{openSections.unassociated && (
				<div className={styles['card-container']}>
					{unassociatedReportTypes.map((field) => {
						return renderCard(field);
					})}
				</div>
			)}

			<div className={styles['pane-section']}>
				<button
					className={`${styles['pane-section__header']} ${outdatedWarning ? styles['pane-section__header--warning'] : ''}`}
					onClick={toggleSection('outdated')}
					type="button"
				>
					<div className={styles['pane-section__header-left']}>
						<div className={styles['pane-section__title-row']}>
							<span className={styles['pane-section__title']}>
								Outdated ({outdatedFields.length})
							</span>
							{outdatedWarning && (
								<Tooltip message={OUTDATED_WARNING_TOOLTIP}>
									<span className={`icon-warning ${styles['pane-section__warning-icon']}`} />
								</Tooltip>
							)}
						</div>
					</div>
					{renderChevron(openSections.outdated)}
				</button>

			</div>

			{openSections.outdated && (
				<div className={styles['card-container']}>
					{outdatedFields.map((name) => {
						return renderOutdatedCard(name);
					})}
				</div>
			)}
		</div>
	);
};

export default React.memo(FieldsPane);
